import { useState, useEffect, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { IH_ACCESS_SCOPE } from "../../services/serviceConfig";

const TOKEN_REFRESH_INTERVAL = 60 * 1000;
const scope = IH_ACCESS_SCOPE;

const useMsalAuth = () => {
    const { instance: msalInstance, inProgress } = useMsal();

    const [isLoadingMsal, setIsLoadingMsal] = useState(true);
    const [accessTokenMsal, setAccessTokenMsal] = useState(undefined);
    const [authenticatedMsal, setAuthenticatedMsal] = useState(false);

    const isRedirecting = useRef(false);
    const refreshInterval = useRef(null);

    const acquireMsalToken = async () => {
        const account = msalInstance.getActiveAccount();
        if (!account) {
            console.warn("No active account found");
            return;
        }

        try {
            const tokenResponse = await msalInstance.acquireTokenSilent({
                account,
                scopes: [scope],
            });

            setAccessTokenMsal(tokenResponse.accessToken);
            setAuthenticatedMsal(true);
        } catch (error) {
            console.error("Silent token acquisition failed:", error);

            if (!isRedirecting.current && inProgress === InteractionStatus.None) {
                isRedirecting.current = true;
                msalInstance.acquireTokenRedirect({
                    scopes: [scope],
                }).catch((err) => {
                    console.error("Token redirect failed:", err);
                    isRedirecting.current = false;
                });
            }
        }
    };

    const logOut = async () => {
        const account = msalInstance.getActiveAccount();
        await msalInstance.logoutRedirect({
            account,
            postLogoutRedirectUri: "/",
        });
    };

    useEffect(() => {
        const init = async () => {
            try {
                const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
                if (account) {
                    msalInstance.setActiveAccount(account);
                    await acquireMsalToken();
                } else if (inProgress === InteractionStatus.None) {
                    isRedirecting.current = true;
                    msalInstance.loginRedirect({
                        scopes: [scope],
                    });
                }
            } catch (err) {
                console.error(err);
            } finally {
                setIsLoadingMsal(false);
            }
        };

        if (inProgress === InteractionStatus.None) {
            init();
        }
    }, [inProgress]);

    useEffect(() => {
        if (authenticatedMsal && !refreshInterval.current) {
            refreshInterval.current = setInterval(() => {
                acquireMsalToken();
            }, TOKEN_REFRESH_INTERVAL);
        }

        return () => {
            if (refreshInterval.current) {
                clearInterval(refreshInterval.current);
                refreshInterval.current = null;
            }
        };
    }, [authenticatedMsal]);

    return {
        authenticatedMsal,
        isLoadingMsal,
        accessTokenMsal,
        logOut,
    };
};

export default useMsalAuth;
